import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import Icon from '../Icon';
import { tokens, inkA, orange } from '../tokens';

// 48px inset search field with a magnifier. The input updates instantly as
// you type, but `onChange` is debounced by `debounce` ms (0 = fire
// immediately) so consumers don't fire an API request on every keystroke.
// It is a type="search" box named by `aria-label` (falls back to the
// placeholder), with an orange ring on keyboard focus.

const Field = styled.input`
  flex: 1;
  width: 100%;
  min-width: 0;
  height: 48px;
  box-sizing: border-box;
  margin: 0;
  padding: 0 18px 0 44px;
  border: 1px solid ${(p) => p.$border};
  border-radius: 8px;
  background: ${(p) => p.$bg};
  color: ${(p) => p.$text};
  color-scheme: ${(p) => p.$scheme};
  font-family: inherit;
  font-size: 14.5px;
  font-weight: 500;
  outline: none;
  -webkit-appearance: none;
  appearance: none;
  transition: border-color 150ms ease, box-shadow 150ms ease;

  &::-webkit-search-decoration {
    -webkit-appearance: none;
  }

  &:focus-visible {
    border-color: ${orange(0.7)};
    box-shadow: 0 0 0 3px ${orange(0.2)};
  }
`;

const SearchInput = ({
  defaultValue = '',
  onChange,
  placeholder,
  debounce = 0,
  'aria-label': ariaLabel,
}) => {
  const theme = useTheme();
  const t = tokens(theme);
  const [text, setText] = useState(defaultValue);
  const timer = useRef(null);

  useEffect(() => () => clearTimeout(timer.current), []);

  const handleChange = (e) => {
    const next = e.target.value;
    setText(next);
    if (typeof onChange !== 'function') return;
    if (debounce > 0) {
      clearTimeout(timer.current);
      timer.current = setTimeout(() => onChange(next), debounce);
    } else {
      onChange(next);
    }
  };

  return (
    <div
      style={{
        position: 'relative',
        display: 'flex',
        flex: 1,
        width: '100%',
        minWidth: 0,
      }}
    >
      <span
        style={{
          position: 'absolute',
          left: 16,
          top: '50%',
          transform: 'translateY(-50%)',
          display: 'flex',
          pointerEvents: 'none',
        }}
      >
        <Icon name="search" size={17} color={inkA(theme, 0.42)} />
      </span>
      <Field
        type="search"
        value={text}
        placeholder={placeholder}
        aria-label={ariaLabel || placeholder}
        onChange={handleChange}
        $border={inkA(theme, 0.1)}
        $bg={t.inset}
        $text={t.text}
        $scheme={theme && theme.name === 'dark' ? 'dark' : 'light'}
      />
    </div>
  );
};

SearchInput.propTypes = {
  defaultValue: PropTypes.string,
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  debounce: PropTypes.number,
  // The box's accessible name; defaults to the placeholder.
  'aria-label': PropTypes.string,
};

export default SearchInput;
